import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, Plus, X } from 'lucide-react';

export default function SearchableSelect({ 
  options = [], 
  value, 
  onChange, 
  fetchOptions,
  debounceMs = 400,
  minSearchLength = 0,
  fetchOnOpen = true,
  loadingText = 'Loading...',
  noDataText = 'Tidak ada data',
  errorText = 'Gagal memuat data',
  placeholder = 'Pilih...', 
  className = '',
  disabled = false,
  displayKey = 'name',
  valueKey = 'id',
  searchable = true,
  size = 'md', // 'sm' for pagination, 'md' for forms
  dropUp = false, // true = buka ke atas, false = buka ke bawah
  onAddNew = null,
  addNewLabel = '',
  maxOptionsHeight = 'max-h-48', // Default fits 4-5 items
  disableAutoFocus = true, // Default true to prevent auto-focusing search input on open (avoids keyboard popup on mobile/tablet)
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState(null);

  // Inline Quick Add state
  const [isInlineAdding, setIsInlineAdding] = useState(false);
  const [inlineValue, setInlineValue] = useState('');
  const [isSubmittingInline, setIsSubmittingInline] = useState(false);
  // Items added via inline quick add — always merged so they show instantly
  const [localAddedItems, setLocalAddedItems] = useState([]);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const inlineInputRef = useRef(null);
  const requestIdRef = useRef(0);

  // Combine options prop, remoteOptions, and locally added items
  // localAddedItems always wins — guarantees instant visibility after inline add
  const allAvailableOptions = React.useMemo(() => {
    const map = new Map();
    (options || []).forEach(opt => {
      if (opt && opt[valueKey] !== undefined) map.set(opt[valueKey], opt);
    });
    (remoteOptions || []).forEach(opt => {
      if (opt && opt[valueKey] !== undefined) map.set(opt[valueKey], opt);
    });
    (localAddedItems || []).forEach(opt => {
      if (opt && opt[valueKey] !== undefined) map.set(opt[valueKey], opt);
    });
    return Array.from(map.values());
  }, [options, remoteOptions, localAddedItems, valueKey]);

  const selectedOption = allAvailableOptions.find(opt => opt[valueKey] === value);
  const displayValue = selectedOption ? selectedOption[displayKey] : placeholder;

  const usingRemote = typeof fetchOptions === 'function' && searchTerm.length >= minSearchLength;
  const baseOptions = usingRemote ? remoteOptions : allAvailableOptions;

  const filteredOptions = searchable && !usingRemote
    ? baseOptions.filter(option => option[displayKey]?.toLowerCase().includes(searchTerm.toLowerCase()))
    : baseOptions;

  // Clean label without double '+' symbol
  const cleanAddNewLabel = (addNewLabel || '').replace(/^\+\s*/, '');

  // Size-based styling
  const buttonPadding = size === 'sm' ? 'px-3 py-1' : size === 'sales' ? 'px-4 py-2.5' : 'px-4 py-2';
  const buttonTextSize = size === 'sm' ? 'text-sm' : size === 'sales' ? 'text-sm' : 'text-base';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  useEffect(() => {
    if (typeof fetchOptions !== 'function') return;
    if (!isOpen) return;
    if (!fetchOnOpen) return;
    if (minSearchLength > 0) return;
    if (remoteOptions.length > 0) return;

    const currentRequestId = ++requestIdRef.current;
    setRemoteLoading(true);
    setRemoteError(null);

    Promise.resolve(fetchOptions(''))
      .then((result) => {
        if (requestIdRef.current !== currentRequestId) return;
        setRemoteOptions(Array.isArray(result) ? result : []);
      })
      .catch((error) => {
        if (requestIdRef.current !== currentRequestId) return;
        setRemoteError(error ?? new Error('Fetch failed'));
        setRemoteOptions([]);
      })
      .finally(() => {
        if (requestIdRef.current !== currentRequestId) return;
        setRemoteLoading(false);
      });
  }, [fetchOptions, fetchOnOpen, isOpen, minSearchLength, remoteOptions.length]);

  useEffect(() => {
    if (typeof fetchOptions !== 'function') return;
    if (!isOpen) return;
    if (fetchOnOpen && minSearchLength === 0 && searchTerm === '') return;

    if (searchTerm.length < minSearchLength) {
      requestIdRef.current += 1;
      setRemoteLoading(false);
      setRemoteError(null);
      return;
    }

    const handle = setTimeout(() => {
      const currentRequestId = ++requestIdRef.current;
      setRemoteLoading(true);
      setRemoteError(null);

      Promise.resolve(fetchOptions(searchTerm))
        .then((result) => {
          if (requestIdRef.current !== currentRequestId) return;
          setRemoteOptions(Array.isArray(result) ? result : []);
        })
        .catch((error) => {
          if (requestIdRef.current !== currentRequestId) return;
          setRemoteError(error ?? new Error('Fetch failed'));
          setRemoteOptions([]);
        })
        .finally(() => {
          if (requestIdRef.current !== currentRequestId) return;
          setRemoteLoading(false);
        });
    }, debounceMs);

    return () => clearTimeout(handle);
  }, [debounceMs, fetchOptions, isOpen, minSearchLength, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setIsInlineAdding(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && !disableAutoFocus) {
      searchInputRef.current.focus();
    }
  }, [isOpen, disableAutoFocus]);

  useEffect(() => {
    if (isInlineAdding && inlineInputRef.current) {
      inlineInputRef.current.focus();
    }
  }, [isInlineAdding]);

  const handleSelect = (option) => {
    onChange(option[valueKey], option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleStartInlineAdd = (initialText = '') => {
    setIsOpen(false);
    setInlineValue(initialText || searchTerm || '');
    setSearchTerm('');
    setIsInlineAdding(true);
  };

  const handleInlineSubmit = async () => {
    if (!inlineValue.trim() || isSubmittingInline) return;
    if (typeof onAddNew !== 'function') return;

    setIsSubmittingInline(true);
    try {
      const createdItem = await onAddNew(inlineValue.trim());
      setIsInlineAdding(false);
      setInlineValue('');
      if (createdItem && createdItem[valueKey] !== undefined) {
        // 1. Inject into localAddedItems — always merged into allAvailableOptions
        setLocalAddedItems(prev => [
          createdItem,
          ...prev.filter(o => o[valueKey] !== createdItem[valueKey]),
        ]);
        // 2. Select it immediately via onChange
        onChange(createdItem[valueKey], createdItem);
      }
    } catch (err) {
      console.error("Failed to add inline option:", err);
    } finally {
      setIsSubmittingInline(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full ${buttonPadding} ${buttonTextSize} text-left border rounded-lg flex items-center justify-between transition ${
          disabled 
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300' 
            : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
        } focus:outline-none focus:ring-2 focus:ring-teal-500`}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>{displayValue}</span>
        <ChevronDown className={`${iconSize} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-[9999] w-full bg-white border border-gray-300 rounded-lg shadow-2xl max-h-56 overflow-hidden flex flex-col ${
          dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}>
          {searchable && (
            <div className="p-2 border-b border-gray-200 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Cari..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
          )}
          <div className={`overflow-y-auto ${maxOptionsHeight} flex-1`}>
            {usingRemote && remoteLoading ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {loadingText}
              </div>
            ) : usingRemote && remoteError ? (
              <div className="px-4 py-8 text-center text-red-600 text-sm">
                {errorText}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                <p className="mb-2">{noDataText}</p>
                {onAddNew && (
                  <button
                    type="button"
                    onClick={() => handleStartInlineAdd(searchTerm)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-600 font-semibold text-xs rounded-lg hover:bg-teal-100 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{cleanAddNewLabel || (searchTerm.trim() ? `Tambah "${searchTerm}"` : "Tambah Baru")}</span>
                  </button>
                )}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option[valueKey]}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between transition ${
                    option[valueKey] === value ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  <span>{option[displayKey]}</span>
                  {option[valueKey] === value && (
                    <Check className="w-4 h-4 text-teal-600" />
                  )}
                </button>
              ))
            )}
          </div>
          {onAddNew && (
            <div className="p-2 border-t border-gray-100 bg-slate-50 flex-shrink-0">
              <button
                type="button"
                onClick={() => handleStartInlineAdd(searchTerm)}
                className="w-full px-3 py-2 text-left text-xs font-semibold text-teal-600 hover:text-teal-700 hover:bg-teal-100/60 rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{cleanAddNewLabel || (searchTerm.trim() ? `Tambah "${searchTerm}" Baru` : "Tambah Baru")}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Inline Field Tambah Data Baru (Langsung di bawah Dropdown) */}
      {isInlineAdding && (
        <div className="mt-2 p-3 bg-teal-50/80 border border-teal-200 rounded-xl space-y-2 animate-in fade-in duration-150 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-teal-900">
              {cleanAddNewLabel || "Tambah Data Baru"}
            </span>
            <button
              type="button"
              onClick={() => {
                setIsInlineAdding(false);
                setInlineValue('');
              }}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <input
              ref={inlineInputRef}
              type="text"
              value={inlineValue}
              onChange={(e) => setInlineValue(e.target.value)}
              placeholder="Masukkan nama baru..."
              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
              autoFocus
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await handleInlineSubmit();
                }
              }}
            />
            <button
              type="button"
              disabled={isSubmittingInline || !inlineValue.trim()}
              onClick={handleInlineSubmit}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1"
            >
              {isSubmittingInline ? "Simpan..." : "Simpan"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

